<template>
  <div class="m-4">
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <h5 class="mb-0">Taxonomies</h5>
      <b-button
        class="pt-2 pb-2"
        size="sm"
        @click="addTaxonomy"
        variant="primary"
      >
        New
      </b-button>
    </div>
    <div class="d-flex flex-row justify-content-end align-items-center mb-3">
      <b-form-group class="text-left mb-0">
        <b-form-input
          size="sm"
          placeholder="Search"
          v-model="search"
        ></b-form-input>
      </b-form-group>
    </div>
    <grid
      :groupFeature="false"
      :columns="columns"
      :autoHeight="true"
      :data="items"
      :columnLines="false"
      :searchFeature="true"
      :stripeFeature="false"
      @storesort="listeners.storeSort"
    ></grid>
    <bottom-paginator
      :total="total"
      :page="paginator.page"
      :perPage="paginator.perPage"
      @change-page="changePage"
    />
    <b-modal
      centered
      ref="form-modal"
      :title="formId ? 'Edit Taxonomy' : 'Add Taxonomy'"
      header-bg-variant="primary"
      header-text-variant="white"
      @ok="submitTaxonomy"
      no-close-on-backdrop
      header-class="bg-purple"
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button variant="primary" @click="ok()">
          Save
        </b-button>
      </template>
      <template v-if="formId" v-slot:modal-header>
        <div class="w-100 d-flex flex-row justify-content-between">
          <h5 class="modal-title">
            {{ formId ? "Edit Taxonomy" : "Add Taxonomy" }}
          </h5>
          <div class="d-flex align-items-center">
            <button
              type="button"
              aria-label="Close"
              class="close text-white"
              @click="$refs['form-modal'].hide()"
            >
              ×
            </button>
          </div>
        </div>
      </template>
      <b-form-group label="Code">
        <b-form-input v-model="form.code" maxLength="40"></b-form-input>
      </b-form-group>
      <b-form-group label="Name">
        <b-form-input v-model="form.name" maxLength="300"></b-form-input>
      </b-form-group>
      <b-form-group label="Type">
        <v-select v-model="form.type" :options="taxonomyTypes"></v-select>
      </b-form-group>
      <b-form-group label="Specialty">
        <v-select
          v-model="form.specialty"
          :options="taxonomySpecialties"
        ></v-select>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import Grid from "../../third_party/grid/Grid.vue";
import taxonomyServer from "../../server/taxonomy";
import BottomPaginator from "../controls/BottomPaginator.vue";
import { mapActions, mapState } from "vuex";

export default {
  name: "TaxonomyCrud",
  components: {
    Grid,
    BottomPaginator
  },
  data() {
    const self = this;
    return {
      list: [],
      formId: null,
      form: {
        code: "",
        name: "",
        type: null,
        specialty: null
      },
      search: "",
      total: 0,
      paginator: {
        page: 0,
        perPage: 10
      },
      orderBy: null,
      orderByDescending: false,
      timeout: null,
      listeners: {
        storeSort: sortData => {
          if (sortData.sorters.length == 1) {
            const sorter = sortData.sorters[0];
            const ascending = sorter.ascending;
            const field = sorter.field;
            self.orderBy = field;
            self.orderByDescending = !ascending;
            self.changePage(0);
          }
        }
      }
    };
  },
  mounted() {
    this.getTaxonomies();
    this.fetchTaxonomyTypes();
    this.fetchTaxonomySpecialties();
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    ...mapActions("client", ["fetchTaxonomyTypes", "fetchTaxonomySpecialties"]),
    async getTaxonomies() {
      const result = await taxonomyServer.query({
        searchPhrase: this.search,
        page: this.paginator.page,
        pageSize: this.paginator.perPage
      });
      this.list = result.records;
      this.total = result.count;
    },
    async submitTaxonomy() {
      const formData = {
        code: this.form.code,
        name: this.form.name,
        taxonomyTypeId: this.form.type ? this.form.type.value : null,
        taxonomySpecialtyId: this.form.specialty
          ? this.form.specialty.value
          : null
      };
      if (this.formId) {
        await taxonomyServer.update(this.formId, formData);
      } else {
        await taxonomyServer.create(formData);
      }
      this.getTaxonomies();
      this.showAlertSuccess();
    },
    addTaxonomy() {
      this.formId = null;
      this.form = {
        code: "",
        description: ""
      };
      this.$refs["form-modal"].show();
    },
    changePage(page) {
      this.paginator.page = page;
      this.getTaxonomies();
    }
  },
  computed: {
    ...mapState("client", {
      taxonomyTypes: state =>
        state.taxonomyTypes.map(taxonomyType => ({
          value: taxonomyType.id,
          label: taxonomyType.name
        })),
      taxonomySpecialties: state =>
        state.taxonomySpecialties.map(taxonomySpecialty => ({
          value: taxonomySpecialty.id,
          label: taxonomySpecialty.name
        }))
    }),
    items() {
      return this.list.map(item => ({
        ...item
      }));
    },
    columns() {
      let columns = [
        {
          text: "Code",
          field: "code",
          flex: 1,
          editor: false,
          sortable() {
            return self.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Name",
          field: "name",
          flex: 1,
          editor: false,
          sortable() {
            return self.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Type",
          field: "taxonomyType.name",
          flex: 1,
          editor: false,
          type: "template",
          template: data =>
            data.record.data.taxonomyType
              ? data.record.data.taxonomyType.name
              : "",
          sortable() {
            return self.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Specialty",
          field: "taxonomySpecialty.name",
          flex: 1,
          editor: false,
          type: "template",
          template: data =>
            data.record.data.taxonomySpecialty
              ? data.record.data.taxonomySpecialty.name
              : "",
          sortable() {
            return self.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "",
          type: "action",
          cellCls: "pointer-cell",
          field: "action",
          align: "right",
          width: 110,
          actions: [
            {
              tooltip: "Edit",
              renderer: () =>
                `<img class="b-action-item" src="${
                  this.isStandalone()
                    ? `/icons/edit.svg`
                    : `/desktop/icons/edit.svg`
                }">`,
              onClick: async ({ record }) => {
                this.formId = record.id;
                const form = await taxonomyServer.get(this.formId);
                this.form = {
                  ...form,
                  type: form.taxonomyType
                    ? {
                        value: form.taxonomyType.id,
                        label: form.taxonomyType.name
                      }
                    : null,
                  specialty: form.taxonomySpecialty
                    ? {
                        value: form.taxonomySpecialty.id,
                        label: form.taxonomySpecialty.name
                      }
                    : null
                };
                this.$refs["form-modal"].show();
              }
            },
            {
              tooltip: "Delete",
              renderer: () =>
                `<img class="b-action-item ml-2 mr-1" src="${
                  this.isStandalone()
                    ? `/icons/delete.svg`
                    : `/desktop/icons/delete.svg`
                }">`,
              onClick: ({ record }) => {
                this.$dialog
                  .confirm(
                    {
                      title: "Are you sure you want to delete this record?"
                    },
                    {
                      view: "confirm-delete"
                    }
                  )
                  .then(async () => {
                    await taxonomyServer.delete(record.id);
                    this.getTaxonomies();
                    this.showAlertSuccess();
                  })
                  .catch(function() {});
              }
            }
          ]
        }
      ];
      return columns;
    }
  },
  watch: {
    search: function() {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }

      this.timeout = setTimeout(() => {
        this.paginator = { page: 0, perPage: 10 };
        this.getTaxonomies();
      }, 1000);
    }
  }
};
</script>
